import { useCallback, useEffect, useRef, useState } from "react";
import doctorService from "../doctorService";
import { DOCTOR_RATING_PAGE_SIZE } from "../doctorProfile";
import { IDoctorRating } from "../interface/doctor.interface";

/**
 * A doctor's ratings, one page at a time. State belongs to the doctor code, so switching doctors starts
 * from an empty list instead of briefly showing the previous doctor's ratings. `loadMore` ignores calls
 * while a page is in flight or once the last page is in.
 */
const useGetDoctorRatings = (doctorCode?: string, enabled = true) => {
  const [items, setItems] = useState<IDoctorRating[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [nextPage, setNextPage] = useState<number>(0);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>();
  const loadingRef = useRef<boolean>(false);
  // Bumped on every reset; a page that comes back for an older generation is thrown away.
  const generationRef = useRef<number>(0);

  const fetchPage = useCallback(
    async (page: number) => {
      if (!doctorCode || loadingRef.current) {
        return;
      }

      const generation = generationRef.current;
      loadingRef.current = true;
      setLoading(true);

      try {
        const response = await doctorService.getRatings(doctorCode, page, DOCTOR_RATING_PAGE_SIZE);

        if (generation !== generationRef.current) {
          return;
        }

        setItems((previous) => (page === 0 ? [] : previous).concat(response.data.content_page ?? []));
        setTotalRecords(response.data.total_records ?? 0);
        setTotalPage(response.data.total_page ?? 1);
        setNextPage(page + 1);
      } catch (error) {
        if (generation === generationRef.current) {
          setError(error);
        }
      } finally {
        if (generation === generationRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [doctorCode],
  );

  useEffect(() => {
    generationRef.current += 1;
    loadingRef.current = false;
    setItems([]);
    setTotalRecords(0);
    setNextPage(0);
    setTotalPage(1);
    setError(undefined);
    setLoading(false);

    if (enabled) {
      fetchPage(0);
    }
  }, [enabled, fetchPage]);

  const hasMore = nextPage < totalPage;

  const loadMore = useCallback(() => {
    if (hasMore) {
      fetchPage(nextPage);
    }
  }, [fetchPage, hasMore, nextPage]);

  return { items, totalRecords, isLoading, error, hasMore, loadMore };
};

export default useGetDoctorRatings;
